import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Ear, CheckCircle, PencilSimple, ArrowsClockwise, Warning, ClockCounterClockwise, SquaresFour, Square, Camera } from '@phosphor-icons/react';
import * as THREE from 'three';
import DiaporamaModal from './DiaporamaModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Silicone products: 4 color options (blue opaque, red opaque, grey transparent, pink transparent)
const SILICONE_COLORS = [
  { name: 'Bleu', hex: '#3B7DD8', shininess: 60, opacity: 1 },
  { name: 'Rouge', hex: '#CC3333', shininess: 60, opacity: 1 },
  { name: 'Gris clair', hex: '#C0C8D0', shininess: 80, opacity: 0.45 },
  { name: 'Rose pastel', hex: '#E8C8C8', shininess: 70, opacity: 0.4 },
];

// Resin 3D products: only grey transparent
const RESIN_COLORS = [
  { name: 'Gris clair', hex: '#C0C8D0', shininess: 80, opacity: 0.45 },
];

function getProductColors(productName) {
  if (!productName) return SILICONE_COLORS;
  const lower = productName.toLowerCase();
  if (lower.includes('resine') || lower.includes('résine') || lower.includes('resin') || lower.includes('3d')) {
    return RESIN_COLORS;
  }
  return SILICONE_COLORS;
}

function STLViewer({ url, color, shininess, opacity, sharedRotationRef, sharedDistanceRef, mirror }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const meshRef = useRef(null);
  const frameRef = useRef(null);
  const isDragging = useRef(false);
  const prevMouse = useRef({ x: 0, y: 0 });
  // Use shared refs when provided so two viewers can rotate/zoom in sync (mirror flag inverts azimuth)
  const localRotation = useRef({ x: -Math.PI / 6, y: Math.PI / 4 });
  const localDistance = useRef(5);
  const rotation = sharedRotationRef || localRotation;
  const distance = sharedDistanceRef || localDistance;
  const mirrorFactor = mirror ? -1 : 1;

  const initScene = useCallback(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let w = mount.clientWidth;
    let h = mount.clientHeight;
    // Fallback if height is 0 (flex layout not yet computed)
    if (h < 10) h = 480;
    if (w < 10) w = 800;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe8e8e8);

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    camera.position.set(0, 0, 5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    mount.appendChild(renderer.domElement);

    // Enhanced lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(5, 8, 5);
    key.castShadow = true;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xddeeff, 0.4);
    fill.position.set(-5, 3, -3);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 0.2);
    rim.position.set(0, -3, -5);
    scene.add(rim);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      // Continuously sync camera position from the shared rotation/distance refs
      // so that two viewers stay in lock-step when their refs are shared.
      const d = distance.current;
      const rx = rotation.current.x;
      const ry = rotation.current.y * mirrorFactor;
      camera.position.set(
        d * Math.sin(ry) * Math.cos(rx),
        d * Math.sin(rx),
        d * Math.cos(ry) * Math.cos(rx)
      );
      const upY = Math.cos(rx) >= 0 ? 1 : -1;
      camera.up.set(0, upY, 0);
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const w2 = mount.clientWidth;
      const h2 = mount.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener('resize', handleResize);
    mount._cleanup = () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameRef.current);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  const updateCamera = useCallback(() => {
    if (!cameraRef.current) return;
    const d = distance.current;
    const rx = rotation.current.x;
    // Apply mirror on azimuth (Y) so that rotating the right ear mirrors the left ear naturally
    const ry = rotation.current.y * mirrorFactor;
    // Standard spherical → cartesian with full 360° freedom on both axes (no clamp)
    cameraRef.current.position.set(
      d * Math.sin(ry) * Math.cos(rx),
      d * Math.sin(rx),
      d * Math.cos(ry) * Math.cos(rx)
    );
    // Keep "up" vector flipping cleanly when crossing poles so the model can rotate infinitely
    const upY = Math.cos(rx) >= 0 ? 1 : -1;
    cameraRef.current.up.set(0, upY, 0);
    cameraRef.current.lookAt(0, 0, 0);
  }, [mirrorFactor, distance, rotation]);

  useEffect(() => {
    initScene();
    return () => { mountRef.current?._cleanup?.(); };
  }, [initScene]);

  // Load STL
  useEffect(() => {
    if (!url || !sceneRef.current) return;
    (async () => {
      try {
        const res = await fetch(url);
        const buffer = await res.arrayBuffer();
        const geometry = parseSTL(buffer);
        geometry.computeVertexNormals();
        geometry.center();
        geometry.computeBoundingBox();
        const bbox = geometry.boundingBox;
        const size = bbox ? bbox.getSize(new THREE.Vector3()) : new THREE.Vector3(1, 1, 1);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const scale = 3 / maxDim;
        geometry.scale(scale, scale, scale);

        const material = new THREE.MeshPhongMaterial({
          color: color || 0x4A90D9,
          shininess: shininess || 60,
          transparent: opacity < 1,
          opacity: opacity || 1,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geometry, material);

        if (meshRef.current) sceneRef.current.remove(meshRef.current);
        sceneRef.current.add(mesh);
        meshRef.current = mesh;

        // Reset camera to good viewing angle
        distance.current = 6;
        rotation.current = { x: -Math.PI / 6, y: Math.PI / 4 };
        updateCamera();
      } catch (e) {
        console.error('STL load error:', e);
      }
    })();
  }, [url, updateCamera]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update material properties
  useEffect(() => {
    if (!meshRef.current) return;
    const mat = meshRef.current.material;
    mat.color.set(color);
    mat.shininess = shininess || 60;
    mat.transparent = opacity < 1;
    mat.opacity = opacity || 1;
    mat.depthWrite = opacity >= 1;
    mat.needsUpdate = true;
  }, [color, shininess, opacity]);

  const onPointerDown = (e) => {
    isDragging.current = true;
    const pt = e.touches ? e.touches[0] : e;
    prevMouse.current = { x: pt.clientX, y: pt.clientY };
  };
  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    const dx = pt.clientX - prevMouse.current.x;
    const dy = pt.clientY - prevMouse.current.y;
    // Mirror inverts the horizontal drag direction so the OG follows the OD naturally
    rotation.current.y -= (dx * 0.008) * mirrorFactor;
    // Rotation X est désormais libre (pas de clamp) → on peut basculer le modèle à l'infini
    rotation.current.x += dy * 0.008;
    prevMouse.current = { x: pt.clientX, y: pt.clientY };
    updateCamera();
  };
  const onPointerUp = () => { isDragging.current = false; };
  const onWheel = (e) => {
    distance.current = Math.max(2, Math.min(15, distance.current + e.deltaY * 0.01));
    updateCamera();
  };

  return (
    <div ref={mountRef} className="w-full touch-none select-none"
      style={{ cursor: isDragging.current ? 'grabbing' : 'grab', height: '100%', minHeight: '400px' }}
      onMouseDown={onPointerDown} onMouseMove={onPointerMove}
      onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
      onTouchStart={onPointerDown} onTouchMove={onPointerMove}
      onTouchEnd={onPointerUp} onWheel={onWheel}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

function parseSTL(buffer) {
  const header = new Uint8Array(buffer, 0, 80);
  const headerStr = String.fromCharCode.apply(null, header);
  if (headerStr.startsWith('solid') && buffer.byteLength > 84) {
    const dv = new DataView(buffer);
    const triangles = dv.getUint32(80, true);
    const expectedSize = 84 + triangles * 50;
    if (Math.abs(expectedSize - buffer.byteLength) > 10) return parseSTLAscii(buffer);
  }
  return parseSTLBinary(buffer);
}

function parseSTLBinary(buffer) {
  const dv = new DataView(buffer);
  const triangles = dv.getUint32(80, true);
  const positions = new Float32Array(triangles * 9);
  const normals = new Float32Array(triangles * 9);
  let offset = 84;
  for (let i = 0; i < triangles; i++) {
    const nx = dv.getFloat32(offset, true); offset += 4;
    const ny = dv.getFloat32(offset, true); offset += 4;
    const nz = dv.getFloat32(offset, true); offset += 4;
    for (let j = 0; j < 3; j++) {
      const idx = i * 9 + j * 3;
      positions[idx] = dv.getFloat32(offset, true); offset += 4;
      positions[idx + 1] = dv.getFloat32(offset, true); offset += 4;
      positions[idx + 2] = dv.getFloat32(offset, true); offset += 4;
      normals[idx] = nx; normals[idx + 1] = ny; normals[idx + 2] = nz;
    }
    offset += 2;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  return geometry;
}

function parseSTLAscii(buffer) {
  const text = new TextDecoder().decode(buffer);
  const positions = [];
  const vertexRegex = /vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)/g;
  let match;
  while ((match = vertexRegex.exec(text)) !== null) {
    positions.push(parseFloat(match[1]), parseFloat(match[2]), parseFloat(match[3]));
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  return geometry;
}

export default function Viewer3DPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [selectedPresetSibling, setSelectedPresetSibling] = useState(0);
  const [showValidate, setShowValidate] = useState(false);
  const [showModify, setShowModify] = useState(false);
  const [modComment, setModComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [dualView, setDualView] = useState(false);
  // Diaporama photos design — chargées via le token viewer STL (accès public).
  const [diaporamaImages, setDiaporamaImages] = useState([]);
  const [diaporamaOpen, setDiaporamaOpen] = useState(false);
  // Shared refs : rotation/zoom synchronisés entre les 2 STLViewers en vue côte à côte.
  // Le sibling utilise mirror=true → la rotation horizontale est appliquée en miroir
  // (faire pivoter l'OD fait pivoter l'OG dans le sens opposé, comme un reflet).
  const sharedRotationRef = useRef({ x: -Math.PI / 6, y: Math.PI / 4 });
  const sharedDistanceRef = useRef(5);

  const availableColors = data ? getProductColors(data.product_name) : SILICONE_COLORS;
  // For impressions (moulages), pre-select the side-based color: blue opaque for left, red opaque for right.
  const sideDefaultIndex = useCallback((side) => {
    if (!side) return 0;
    if (side === 'left') return 0;  // Bleu opaque
    if (side === 'right') return 1; // Rouge opaque
    return 0;
  }, []);

  // Once data + siblings are loaded, set the appropriate default color for moulages.
  useEffect(() => {
    if (!data) return;
    if (data.is_impression) {
      setSelectedPreset(sideDefaultIndex(data.side));
    }
  }, [data, sideDefaultIndex]);

  useEffect(() => {
    if (!data || !siblings.length) return;
    if (data.is_impression) {
      setSelectedPresetSibling(sideDefaultIndex(siblings[0]?.side));
    }
  }, [data, siblings, sideDefaultIndex]);
  const preset = availableColors[selectedPreset] || availableColors[0];
  const presetSibling = availableColors[selectedPresetSibling] || availableColors[0];
  const modelUrl = `${API}/api/viewer/${token}/model`;

  // Primary side is the one from the current token; sibling is the first remaining side
  const sibling = siblings.length > 0 ? siblings[0] : null;
  const siblingModelUrl = sibling ? `${API}/api/viewer/${sibling.token}/model` : null;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/viewer/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Ce lien est invalide ou a expire.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  // Fetch sibling tokens (other sides of same order) once primary data is loaded
  useEffect(() => {
    if (!data) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/viewer/${token}/siblings`);
        setSiblings(res.data || []);
      } catch {
        setSiblings([]);
      }
    })();
  }, [data, token]);

  // Fetch photos design (diaporama) — public via le token viewer.
  useEffect(() => {
    if (!data) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/viewer/${token}/diaporama`);
        setDiaporamaImages(res.data?.images || []);
      } catch {
        setDiaporamaImages([]);
      }
    })();
  }, [data, token]);

  const handleValidate = async () => {
    setSubmitting(true);
    try {
      // Validate primary token AND any sibling tokens so the client doesn't have
      // to make two separate clicks when both sides are shown side by side.
      const tokensToValidate = [token, ...siblings.map(s => s.token).filter(Boolean)];
      for (const t of tokensToValidate) {
        try {
          await axios.post(`${API}/api/viewer/${t}/validate`);
        } catch (innerErr) {
          // Tolérer l'erreur "déjà validé" (400) — on continue le flux
          const code = innerErr.response?.status;
          if (code !== 400 && code !== 410) throw innerErr;
        }
      }
      setResult('validated');
      setShowValidate(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const handleModification = async () => {
    if (!modComment.trim()) return;
    setSubmitting(true);
    try {
      // Envoyer la demande de modification pour le côté primaire ET les siblings —
      // le commentaire est identique pour les 2 côtés vus simultanément.
      const tokensToModify = [token, ...siblings.map(s => s.token).filter(Boolean)];
      for (const t of tokensToModify) {
        try {
          await axios.post(`${API}/api/viewer/${t}/request-modification`, { comment: modComment });
        } catch (innerErr) {
          const code = innerErr.response?.status;
          if (code !== 400 && code !== 410) throw innerErr;
        }
      }
      setResult('modified');
      setShowModify(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 p-8 max-w-md text-center">
        <Warning size={48} className="mx-auto mb-4 text-amber-500" weight="duotone" />
        <h2 className="font-heading font-bold text-lg mb-2">Lien indisponible</h2>
        <p className="text-sm text-zinc-600">{error}</p>
      </div>
    </div>
  );

  if (result) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 p-8 max-w-md text-center" data-testid="viewer-result">
        <CheckCircle size={48} className="mx-auto mb-4 text-emerald-500" weight="fill" />
        <h2 className="font-heading font-bold text-lg mb-2">
          {result === 'validated' ? 'Modele valide !' : 'Demande envoyee'}
        </h2>
        <p className="text-sm text-zinc-600">
          {result === 'validated'
            ? 'Votre validation a ete enregistree. La fabrication va pouvoir demarrer.'
            : 'Votre demande de modification a ete transmise a notre equipe technique.'}
        </p>
      </div>
    </div>
  );

  const isValidated = data.status === 'validated';
  const isImpression = !!data.is_impression;

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" onContextMenu={(e) => e.preventDefault()}>
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Ear size={24} weight="duotone" className="text-white" />
            <span className="font-heading font-bold text-sm tracking-widest">HAMAC SOLUTIONS</span>
          </div>
          {/* Bouton diaporama — centre du header, visible seulement s'il y a des photos */}
          {diaporamaImages.length > 0 && (
            <button
              type="button"
              onClick={() => setDiaporamaOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-sm bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors border border-white/20"
              data-testid="viewer-diaporama-open"
            >
              <Camera size={16} />
              Voir les photos ({diaporamaImages.length})
            </button>
          )}
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Visualisation 3D</span>
        </div>
      </header>

      {/* Info bar */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-zinc-500">{data.order_reference}</span>
            <span className="font-medium">{data.side_label}</span>
            <span className="text-zinc-400">v{data.version}</span>
          </div>
          <div className="flex items-center gap-2">
            {isImpression && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-sm" data-testid="viewer-impression-badge">
                Empreinte scannée
              </span>
            )}
            {!isImpression && isValidated && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-sm" data-testid="viewer-validated-badge">
                <CheckCircle size={12} className="mr-1" />Valide
              </span>
            )}
            {sibling && (
              <button onClick={() => setDualView(!dualView)}
                className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm transition-colors ${
                  dualView ? 'bg-zinc-900 text-white hover:bg-zinc-800' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                }`}
                title={dualView ? 'Revenir à une seule vue' : 'Afficher les deux côtés côte à côte'}
                data-testid="dual-view-toggle">
                {dualView ? <Square size={12} className="mr-1" /> : <SquaresFour size={12} className="mr-1" />}
                {dualView ? 'Vue unique' : 'Côte à côte'}
              </button>
            )}
            {data.action_history?.length > 0 && (
              <button onClick={() => setShowHistory(!showHistory)}
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-zinc-100 text-zinc-600 rounded-sm hover:bg-zinc-200 transition-colors"
                data-testid="viewer-history-toggle">
                <ClockCounterClockwise size={12} className="mr-1" />Historique
              </button>
            )}
          </div>
        </div>
        {/* Action history panel */}
        {showHistory && data.action_history?.length > 0 && (
          <div className="border-t border-zinc-100 bg-zinc-50 px-4 py-2 max-w-5xl mx-auto" data-testid="viewer-history">
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {data.action_history.map((a, i) => (
                <div key={`${a.timestamp}-${a.action}`} className="flex items-center gap-3 text-[11px]">
                  <span className="text-zinc-400 font-mono w-28 flex-shrink-0">{new Date(a.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="text-zinc-600">{a.action}</span>
                  {a.details && <span className="text-zinc-400 truncate">{a.details}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 3D Viewer */}
      {dualView && sibling ? (
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-300" style={{ minHeight: '50vh', height: '60vh' }} data-testid="dual-viewer">
          {/* Panel 1 - primary side */}
          <div className="relative bg-zinc-100">
            <div className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-sm border border-zinc-200 shadow-sm">
              <p className="text-[10px] font-medium text-zinc-700">{data.side_label} — v{data.version}</p>
            </div>
            <STLViewer url={modelUrl} color={preset.hex} shininess={preset.shininess} opacity={preset.opacity}
              sharedRotationRef={sharedRotationRef} sharedDistanceRef={sharedDistanceRef} />
            {availableColors.length > 1 && (
              <div className="absolute bottom-3 left-3">
                <div className="bg-white/95 backdrop-blur-sm p-2 rounded-sm border border-zinc-200 shadow-sm">
                  <div className="flex gap-1.5" data-testid="color-picker-primary">
                    {availableColors.map((c, i) => (
                      <button key={c.name} onClick={() => setSelectedPreset(i)}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${selectedPreset === i ? 'border-zinc-900 scale-110' : 'border-zinc-300'}`}
                        style={{ backgroundColor: c.hex, opacity: c.opacity < 1 ? 0.5 : 1 }}
                        title={c.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Panel 2 - sibling side */}
          <div className="relative bg-zinc-100">
            <div className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-sm border border-zinc-200 shadow-sm">
              <p className="text-[10px] font-medium text-zinc-700">{sibling.side_label} — v{sibling.version}</p>
            </div>
            <STLViewer url={siblingModelUrl} color={presetSibling.hex} shininess={presetSibling.shininess} opacity={presetSibling.opacity}
              sharedRotationRef={sharedRotationRef} sharedDistanceRef={sharedDistanceRef}
              mirror={data?.side !== sibling?.side} />
            {availableColors.length > 1 && (
              <div className="absolute bottom-3 left-3">
                <div className="bg-white/95 backdrop-blur-sm p-2 rounded-sm border border-zinc-200 shadow-sm">
                  <div className="flex gap-1.5" data-testid="color-picker-sibling">
                    {availableColors.map((c, i) => (
                      <button key={c.name} onClick={() => setSelectedPresetSibling(i)}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${selectedPresetSibling === i ? 'border-zinc-900 scale-110' : 'border-zinc-300'}`}
                        style={{ backgroundColor: c.hex, opacity: c.opacity < 1 ? 0.5 : 1 }}
                        title={c.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 relative" style={{ minHeight: '50vh', height: '60vh' }}>
          <STLViewer url={modelUrl} color={preset.hex} shininess={preset.shininess} opacity={preset.opacity} />

          {/* Color picker - only if multiple colors available */}
          {availableColors.length > 1 && (
            <div className="absolute bottom-4 left-4">
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-sm border border-zinc-200 shadow-sm" data-testid="color-picker">
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-2 px-0.5">Couleur</p>
                <div className="flex gap-2">
                  {availableColors.map((c, i) => (
                    <button key={c.name} onClick={() => setSelectedPreset(i)}
                      className={`w-9 h-9 rounded-full border-2 transition-all ${selectedPreset === i ? 'border-zinc-900 scale-110 shadow-md' : 'border-zinc-300 hover:border-zinc-400'}`}
                      style={{ backgroundColor: c.hex, opacity: c.opacity < 1 ? 0.5 : 1 }}
                      title={c.name} data-testid={`color-${c.name.toLowerCase().replace(/\s/g, '-')}`}
                    />
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5 px-0.5 font-medium">{preset.name}{preset.opacity < 1 ? ' — transparent' : ''}</p>
              </div>
            </div>
          )}

          {/* Recenter button */}
          <button className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-sm border border-zinc-200 hover:bg-zinc-100"
            onClick={() => window.location.reload()} title="Recentrer" data-testid="recenter-btn">
            <ArrowsClockwise size={18} className="text-zinc-600" />
          </button>
        </div>
      )}

      {/* Actions */}
      {!isValidated && !isImpression && (
        <div className="bg-white border-t border-zinc-200">
          <div className="max-w-5xl mx-auto px-4 py-4">
            {!showValidate && !showModify && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-sm h-12 text-sm font-medium"
                  onClick={() => setShowValidate(true)} data-testid="validate-btn">
                  <CheckCircle size={18} className="mr-2" />Valider le modele
                </Button>
                <Button variant="outline" className="flex-1 rounded-sm h-12 text-sm font-medium border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => setShowModify(true)} data-testid="modify-btn">
                  <PencilSimple size={18} className="mr-2" />Demander une modification
                </Button>
              </div>
            )}
            {showValidate && (
              <div className="space-y-3" data-testid="validate-confirm">
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
                  <p className="text-sm text-emerald-800 font-medium">Je confirme avoir verifie ce modele et le valider pour fabrication.</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 rounded-sm" onClick={() => setShowValidate(false)}>Annuler</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-sm" onClick={handleValidate}
                    disabled={submitting} data-testid="confirm-validate-btn">
                    {submitting ? 'Envoi...' : 'Confirmer la validation'}
                  </Button>
                </div>
              </div>
            )}
            {showModify && (
              <div className="space-y-3" data-testid="modify-form">
                <textarea value={modComment} onChange={(e) => setModComment(e.target.value)}
                  placeholder="Decrivez les modifications souhaitees..."
                  className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  data-testid="modify-comment" />
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 rounded-sm" onClick={() => setShowModify(false)}>Annuler</Button>
                  <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                    onClick={handleModification} disabled={submitting || !modComment.trim()} data-testid="confirm-modify-btn">
                    {submitting ? 'Envoi...' : 'Envoyer la demande'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal diaporama photos design */}
      <DiaporamaModal
        open={diaporamaOpen}
        onClose={() => setDiaporamaOpen(false)}
        images={diaporamaImages}
        getImageUrl={(imgId) => `${API}/api/viewer/${token}/diaporama/${imgId}`}
      />
    </div>
  );
}
